import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// Bulletproof cleanup: strip Emergent platform attribution that may be
// re-injected into index.html during the platform's GitHub push step.
// Runs immediately and again on a short loop to catch late injection.
function stripPlatformAttribution() {
  try {
    // 1. Remove the "Made with Emergent" badge anchor
    document.querySelectorAll('#emergent-badge, a[href*="emergent.sh"]').forEach((el) => el.remove());

    // 2. Override the document title in case it was set to the Emergent default
    if (
      document.title === "Emergent | Fullstack App" ||
      document.title.toLowerCase().includes("emergent")
    ) {
      document.title = "Transformerus — Build full-stack apps with AI";
    }

    // 3. Override meta description if it references emergent.sh
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && /emergent\.sh|product of emergent/i.test(metaDesc.getAttribute("content") || "")) {
      metaDesc.setAttribute(
        "content",
        "Transformerus — Build full-stack web & mobile apps in minutes with AI."
      );
    }

    // 4. Remove the emergent-main analytics script tag if present
    document
      .querySelectorAll('script[src*="emergent.sh"], script[src*="assets.emergent"]')
      .forEach((el) => el.remove());

    // 5. Disable PostHog if it was auto-loaded (no opt-in by user)
    if (typeof window !== "undefined" && window.posthog && typeof window.posthog.opt_out_capturing === "function") {
      try {
        window.posthog.opt_out_capturing();
      } catch (_) {}
    }
  } catch (_) {
    // Never let cleanup throw on the user
  }
}

// Run immediately
stripPlatformAttribution();

// Run again after DOM is fully loaded (badge anchor sits at end of <body>)
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", stripPlatformAttribution);
} else {
  // Schedule a few sweeps to catch any late insertion
  [50, 250, 1000, 3000].forEach((ms) => setTimeout(stripPlatformAttribution, ms));
}

// MutationObserver: instantly remove the badge if injected after page load
if (typeof MutationObserver !== "undefined") {
  const observer = new MutationObserver(() => {
    const badge = document.getElementById("emergent-badge");
    if (badge) badge.remove();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
}

// Unregister any stale Service Worker + clear its caches. Earlier deploys
// (Emergent preview / Vercel) may have registered SWs that aggressively
// cache HTML and JS, causing returning visitors to see old builds even after
// new deploys. Doing this on every load self-heals affected browsers.
if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => Promise.all(regs.map((r) => r.unregister())))
    .catch(() => {});
}
if (typeof caches !== "undefined" && caches.keys) {
  caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
